import * as React from 'react'
import * as Dom from 'react-dom/server'

export interface PostImage {
  id: string
  size: [number, number]
  detached: boolean
  png: string
  webp: string
  png_url: string
  webp_url: string
}

export interface PostContent {
  text: string
  anchors: string[]
  footnotes: Record<string, string>
  images: PostImage[]
}

export interface PostAnchors {
  anchors: string[]
  anchor_urls: string[]
}

export interface PostFootNotes {
  footnotes: Record<string, string>
  footnote_urls: Record<string, string>
}

export interface PostEditors {
  editors: string[]
}

type ToLink = React.ComponentType<{ text: string, href: string }>

function* transformText(text: string,
  anchors: PostAnchors, footnote: PostFootNotes,
  normalize: (s: string) => string, toLink: ToLink): Generator<React.ReactElement> {
  for (let start = 0, end = 0; end < text.length; start = end) {
    let chosenUrl = -1, chosenFootnotes = []
    let chosenFrom = text.length, chosenLength = 0
    for (let i = 0; i < anchors.anchors.length; ++i) {
      const anchorFrom = text.indexOf(anchors.anchors[i], start)
      if (anchorFrom >= start && anchorFrom <= chosenFrom) {
        if (anchorFrom < chosenFrom || anchors.anchors[i].length > chosenLength) {
          chosenLength = anchors.anchors[i].length
          chosenFrom = anchorFrom
          chosenUrl = i
        }
      }
    }
    const footnoteFrom = text.indexOf('[', start)
    if (footnoteFrom >= start && footnoteFrom < chosenFrom) {
      const footnoteTo = text.indexOf(']', footnoteFrom) + 1
      const footnoteParts = text.substring(footnoteFrom + 1, footnoteTo - 1).split(',')
      if (footnoteParts.every(part => `[${part.trim()}]` in footnote.footnotes)) {
        chosenLength = footnoteTo - footnoteFrom
        chosenFootnotes.push(...footnoteParts)
        chosenFrom = footnoteFrom
      }
    }
    const children = [normalize(text.substring(start, chosenFrom))]
    yield React.createElement('span', { key: start, children })
    end = chosenFrom + chosenLength
    if (chosenFootnotes.length > 0) {
      let prefix = '['
      for (const text of chosenFootnotes) {
        const url = new URL(footnote['footnote_urls'][`[${text}]`] ?? '#', location.href)
        const full = url.origin !== location.origin || url.pathname !== location.pathname
        const href = full ? url.href : url.search + url.hash
        yield React.createElement('span', { key: start, children: [prefix] })
        yield React.createElement(toLink, { key: chosenFrom, text, href })
        prefix = ','
      }
      yield React.createElement('span', { key: start, children: [']'] })
    } else if (chosenUrl >= 0) {
      const text = normalize(anchors.anchors[chosenUrl])
      const href = anchors['anchor_urls'][chosenUrl] ?? '#'
      yield React.createElement(toLink, { key: chosenFrom, text, href })
    }
  }
}

export function createRenderer(toLink: ToLink) {
  const denormalize = (s: string) => s.replace(/\s/g, '\u00a0')

  const normalize = (s: string) => s.replace(/\s/g, ' ')

  const fromTextToHtml = (text: string, anchor: PostAnchors, footnote: PostFootNotes) => {
    let inputRaw = ''
    for (let node of transformText(text, anchor, footnote, normalize, toLink)) {
      inputRaw += Dom.renderToStaticMarkup(node)
    }
    const result = document.createElement('span')
    result.innerHTML = inputRaw
    return result
  }

  const fromSelectionToText = (s: Selection | null) => denormalize(s?.toString() ?? '')

  const fromHtmlToText = (h: HTMLSpanElement) => denormalize(h.innerText)

  return { fromTextToHtml, fromSelectionToText, fromHtmlToText }
}
